import { getCurrentIdToken } from '../firebase';
import { getApiUrl } from '../config/apiConfig';

// Cache for the ID token to avoid redundant Firebase SDK calls
let cachedToken: string | null = null;
let tokenExpiry: number = 0;

// In-flight request deduplication map
const inflightRequests = new Map<string, Promise<any>>();

async function getAuthHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  try {
    const now = Date.now();
    // Refresh token if missing or expiring in less than 5 minutes
    if (!cachedToken || now > tokenExpiry - (5 * 60 * 1000)) {
      const token = await getCurrentIdToken();
      if (token) {
        cachedToken = token;
        // Tokens typically last 1 hour, set expiry to 55 mins from now to be safe
        tokenExpiry = now + (55 * 60 * 1000);
      }
    }

    if (cachedToken) {
      headers['Authorization'] = `Bearer ${cachedToken}`;
    }
  } catch (e) {
    console.error('[apiClient] Failed to retrieve auth token:', e);
  }
  return headers;
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { params, ...init } = options;
  const method = init.method || 'GET';
  
  let url = getApiUrl(path);
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += (url.includes('?') ? '&' : '?') + queryString;
    }
  }

  // Deduplicate GET requests
  const cacheKey = `${method}:${url}`;
  if (method === 'GET' && inflightRequests.has(cacheKey)) {
    return inflightRequests.get(cacheKey);
  }

  const performRequest = async () => {
    try {
      const authHeaders = await getAuthHeaders();
      const config = {
        ...init,
        headers: {
          ...authHeaders,
          ...init.headers,
        },
      };

      let response = await fetch(url, config);

      // If token expired (401), force-refresh Firebase token and retry once
      if (response.status === 401) {
        try {
          cachedToken = null;
          tokenExpiry = 0;
          const refreshedToken = await getCurrentIdToken(true);
          if (refreshedToken) {
            cachedToken = refreshedToken;
            tokenExpiry = Date.now() + (55 * 60 * 1000);
            const retryConfig = {
              ...init,
              headers: {
                'Content-Type': 'application/json',
                ...init.headers,
                'Authorization': `Bearer ${refreshedToken}`,
              },
            };
            response = await fetch(url, retryConfig);
          }
        } catch (refreshErr) {
          console.warn('[apiClient] Token refresh retry failed:', refreshErr);
        }
      }

      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // JSON parsing failed
        }
        throw new Error(errorMessage);
      }

      if (response.status === 204) {
        return {} as T;
      }

      return response.json();
    } finally {
      // Clear from inflight map once done
      if (method === 'GET') {
        inflightRequests.delete(cacheKey);
      }
    }
  };

  const requestPromise = performRequest();
  
  if (method === 'GET') {
    inflightRequests.set(cacheKey, requestPromise);
  }

  return requestPromise;
}

export const apiClient = {
  get: <T>(path: string, params?: RequestOptions['params'], options?: RequestInit) => 
    request<T>(path, { ...options, method: 'GET', params }),
  
  post: <T>(path: string, body?: any, params?: RequestOptions['params'], options?: RequestInit) => 
    request<T>(path, { ...options, method: 'POST', body: body ? JSON.stringify(body) : undefined, params }),
  
  put: <T>(path: string, body?: any, params?: RequestOptions['params'], options?: RequestInit) => 
    request<T>(path, { ...options, method: 'PUT', body: body ? JSON.stringify(body) : undefined, params }),
  
  delete: <T>(path: string, params?: RequestOptions['params'], options?: RequestInit) => 
    request<T>(path, { ...options, method: 'DELETE', params }),
};
